/**
 * LiveAvatarVideo — real-time lip-synced avatar powered by LiveAvatar API + LiveKit.
 *
 * Usage:
 *   const avatarRef = useRef(null);
 *   <LiveAvatarVideo ref={avatarRef} fallbackSrc="/my-avatar.png" onSpeakEnd={handleDone} />
 *
 *   // Speak text (returns Promise that resolves when avatar finishes):
 *   await avatarRef.current?.speak("Hello world");
 *
 *   // Interrupt mid-sentence:
 *   avatarRef.current?.interrupt();
 *
 * Imperative handle exposes: { speak, interrupt, status, isSpeaking }
 *   status: "connecting" | "connected" | "error"
 *
 * While connecting, shows fallbackSrc (static image).
 * Falls back gracefully (onSpeakEnd fires immediately) if LiveAvatar is unavailable.
 *
 * Auto-reconnect: if the LiveAvatar session expires or the LiveKit room disconnects
 * unexpectedly the component restarts the session automatically (up to MAX_RECONNECTS
 * attempts). Any text that was being spoken when the drop happened is re-queued so
 * the candidate hears it once the avatar comes back.
 */
import {
  useEffect, useRef, useState,
  useImperativeHandle, forwardRef,
} from "react";
import { Room, RoomEvent, ConnectionState } from "livekit-client";
import { api } from "@/lib/api";

// Max retries for room-level disconnects (LiveKit drops during an active session)
const MAX_RECONNECTS      = 10;
// Max retries for API-level failures (upstream LiveAvatar/HeyGen returning 5xx)
const MAX_API_RETRIES     = 3;
const RECONNECT_DELAY     = 3000;     // base delay — grows with each attempt (capped at 15s)

const LiveAvatarVideo = forwardRef(function LiveAvatarVideo(
  {
    fallbackSrc = "",
    fallbackAlt = "AI Agent",
    className   = "",
    style       = {},
    gender      = "female",
    lang        = "en",
    topic       = "",
    /**
     * Optional custom async function that returns { livekit_url, livekit_client_token }.
     * When provided, overrides the default api.avatarStart({ gender, lang, topic }) call.
     */
    startFn,
    onSpeakStart,
    onSpeakEnd,
    onStatusChange,
  },
  ref,
) {
  const roomRef           = useRef(null);
  const videoRef          = useRef(null);
  const speakResolveRef   = useRef(null);   // resolves the current speak() Promise
  const pendingTextRef    = useRef(null);   // text queued before connection (or during reconnect)
  const isSpeakingRef     = useRef(false);
  const ttsUtterRef       = useRef(null);   // current browser TTS utterance (dev-mode)
  const devModeRef        = useRef(false);  // true when backend says disabled:true
  const onSpeakStartRef   = useRef(onSpeakStart);
  const onSpeakEndRef     = useRef(onSpeakEnd);
  const onStatusChangeRef = useRef(onStatusChange);

  // Reconnect book-keeping
  const reconnectCountRef = useRef(0);
  const apiErrorCountRef  = useRef(0);   // counts consecutive API-level failures (5xx)
  const reconnectTimerRef = useRef(null);
  const cancelledRef      = useRef(false);   // set when the component unmounts

  const [status, setStatus]   = useState("connecting");
  const [devMode, setDevMode] = useState(false); // browser-TTS fallback active
  // Track which reconnect cycle we're on so the useEffect can restart cleanly
  const [connectKey, setConnectKey] = useState(0);

  // Keep callback refs up to date without re-running effects
  useEffect(() => { onSpeakStartRef.current   = onSpeakStart;   }, [onSpeakStart]);
  useEffect(() => { onSpeakEndRef.current     = onSpeakEnd;     }, [onSpeakEnd]);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; }, [onStatusChange]);

  const updateStatus = (s) => {
    setStatus(s);
    onStatusChangeRef.current?.(s);
  };

  // ── helpers ────────────────────────────────────────────────────────────────

  const mkPayload = (eventType, extra = {}) =>
    new TextEncoder().encode(
      JSON.stringify({ event_id: crypto.randomUUID(), event_type: eventType, ...extra }),
    );

  const publishControl = (eventType, extra = {}) => {
    const room = roomRef.current;
    if (!room || room.state !== ConnectionState.Connected) return;
    room.localParticipant
      .publishData(mkPayload(eventType, extra), { topic: "agent-control" })
      .catch(() => {});
  };

  // ── reconnect ─────────────────────────────────────────────────────────────

  const scheduleReconnect = (pendingSpeech) => {
    if (cancelledRef.current) return;
    if (reconnectCountRef.current >= MAX_RECONNECTS) {
      console.warn("[LiveAvatarVideo] max reconnects reached — going to error state");
      updateStatus("error");
      speakResolveRef.current?.();
      speakResolveRef.current = null;
      onSpeakEndRef.current?.();
      return;
    }

    reconnectCountRef.current += 1;
    // Exponential backoff: 3s, 6s, 12s … capped at 15s
    const delay = Math.min(RECONNECT_DELAY * Math.pow(1.5, reconnectCountRef.current - 1), 15000);
    console.info(
      `[LiveAvatarVideo] scheduling reconnect attempt ${reconnectCountRef.current}/${MAX_RECONNECTS} in ${Math.round(delay/1000)}s…`,
    );

    if (pendingSpeech) pendingTextRef.current = pendingSpeech;

    speakResolveRef.current?.();
    speakResolveRef.current = null;
    isSpeakingRef.current   = false;

    try { roomRef.current?.disconnect(); } catch {}
    roomRef.current = null;

    updateStatus("connecting");
    clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = setTimeout(() => {
      if (!cancelledRef.current) setConnectKey((k) => k + 1);
    }, delay);
  };

  // Manual retry — resets all counters and reconnects immediately
  const retryNow = () => {
    reconnectCountRef.current = 0;
    apiErrorCountRef.current  = 0;
    try { roomRef.current?.disconnect(); } catch {}
    roomRef.current = null;
    updateStatus("connecting");
    clearTimeout(reconnectTimerRef.current);
    setConnectKey((k) => k + 1);
  };

  // ── imperative handle ──────────────────────────────────────────────────────

  useImperativeHandle(
    ref,
    () => ({
      speak(text) {
        // ── Dev mode: use browser TTS ──────────────────────────────────────
        if (devModeRef.current) {
          return new Promise((resolve) => {
            if (typeof window === "undefined" || !window.speechSynthesis) {
              resolve(); return;
            }
            window.speechSynthesis.cancel();
            const utter = new SpeechSynthesisUtterance(text);
            utter.rate  = 0.95;
            utter.pitch = 1.05;
            // Try to pick a natural-sounding English voice
            const voices = window.speechSynthesis.getVoices();
            const preferred = voices.find(v => /Samantha|Karen|Moira|Fiona|Victoria|Nicky|Ava|Allison/i.test(v.name))
              || voices.find(v => v.lang.startsWith("en") && !v.localService === false)
              || voices[0];
            if (preferred) utter.voice = preferred;
            ttsUtterRef.current = utter;
            isSpeakingRef.current = true;
            onSpeakStartRef.current?.();
            utter.onend = utter.onerror = () => {
              isSpeakingRef.current = false;
              ttsUtterRef.current   = null;
              onSpeakEndRef.current?.();
              resolve();
            };
            window.speechSynthesis.speak(utter);
          });
        }

        // ── Normal LiveKit mode ────────────────────────────────────────────
        const room = roomRef.current;
        if (!room || room.state !== ConnectionState.Connected) {
          pendingTextRef.current = text;
          return Promise.resolve();
        }
        if (speakResolveRef.current) {
          speakResolveRef.current();
          speakResolveRef.current = null;
          publishControl("avatar.interrupt");
        }
        return new Promise((resolve) => {
          speakResolveRef.current = resolve;
          publishControl("avatar.speak_text", { text });
        });
      },

      interrupt() {
        if (devModeRef.current) {
          window.speechSynthesis?.cancel();
          isSpeakingRef.current = false;
          ttsUtterRef.current   = null;
          return;
        }
        if (speakResolveRef.current) {
          speakResolveRef.current();
          speakResolveRef.current = null;
        }
        isSpeakingRef.current = false;
        publishControl("avatar.interrupt");
      },

      get isSpeaking() { return isSpeakingRef.current; },
      get status()     { return status; },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status],
  );

  // ── LiveKit session lifecycle (re-runs on connectKey change = reconnect) ────

  useEffect(() => {
    cancelledRef.current = false;
    let localCancelled   = false;

    (async () => {
      try {
        let livekitUrl, livekitToken;
        try {
          const result = await (startFn ? startFn() : api.avatarStart({ gender, lang, topic }));

          // Backend flagged avatar as disabled (LIVEAVATAR_DISABLED=true) — switch to browser TTS
          if (result?.disabled) {
            console.info("[LiveAvatarVideo] avatar disabled by server — switching to browser TTS mode");
            devModeRef.current = true;
            setDevMode(true);
            updateStatus("connected"); // treat as "connected" so the rest of the UI works normally
            const queued = pendingTextRef.current;
            pendingTextRef.current = null;
            if (queued && window.speechSynthesis) {
              await new Promise(r => setTimeout(r, 400));
              const utter = new SpeechSynthesisUtterance(queued);
              utter.rate  = 0.95; utter.pitch = 1.05;
              window.speechSynthesis.speak(utter);
            }
            return;
          }

          livekitUrl   = result.livekit_url;
          livekitToken = result.livekit_client_token;
          apiErrorCountRef.current = 0;
        } catch (apiErr) {
          // API-level failure (backend returned 5xx) — limited retries with long backoff
          apiErrorCountRef.current += 1;
          const status = apiErr?.response?.status || apiErr?.status || 0;
          console.error(`[LiveAvatarVideo] API error (attempt ${apiErrorCountRef.current}/${MAX_API_RETRIES}) status=${status}:`, apiErr);
          if (localCancelled || cancelledRef.current) return;
          if (apiErrorCountRef.current >= MAX_API_RETRIES) {
            console.warn("[LiveAvatarVideo] LiveAvatar API consistently failing — going offline");
            updateStatus("error");
            speakResolveRef.current?.();
            speakResolveRef.current = null;
            onSpeakEndRef.current?.();
            return;
          }
          // Retry API call after a longer delay (8s, 16s)
          const apiDelay = Math.min(8000 * apiErrorCountRef.current, 30000);
          console.info(`[LiveAvatarVideo] will retry API in ${apiDelay/1000}s…`);
          updateStatus("connecting");
          reconnectTimerRef.current = setTimeout(() => {
            if (!cancelledRef.current) setConnectKey((k) => k + 1);
          }, apiDelay);
          return;
        }

        if (localCancelled || cancelledRef.current) return;

        const room = new Room();
        roomRef.current = room;

        // Attach avatar video/audio to the <video> element
        room.on(RoomEvent.TrackSubscribed, (track) => {
          if (videoRef.current) track.attach(videoRef.current);
        });
        room.on(RoomEvent.TrackUnsubscribed, (track) => track.detach());

        // ── Handle unexpected room disconnection ──────────────────────────
        room.on(RoomEvent.Disconnected, () => {
          if (localCancelled || cancelledRef.current) return;
          console.warn("[LiveAvatarVideo] LiveKit room disconnected — reconnecting…");
          scheduleReconnect(null);
        });

        // ── Handle server → client events ─────────────────────────────────
        room.on(RoomEvent.DataReceived, (payload, _p, _k, topic) => {
          if (topic !== "agent-response") return;
          try {
            const { event_type } = JSON.parse(new TextDecoder().decode(payload));

            if (event_type === "avatar.speak_started") {
              isSpeakingRef.current = true;
              onSpeakStartRef.current?.();

            } else if (event_type === "avatar.speak_ended") {
              isSpeakingRef.current = false;
              speakResolveRef.current?.();
              speakResolveRef.current = null;
              onSpeakEndRef.current?.();

            } else if (event_type === "session.stopped") {
              // LiveAvatar session expired (server-side timeout) — auto-reconnect
              if (localCancelled || cancelledRef.current) return;
              console.warn("[LiveAvatarVideo] session.stopped received — restarting session…");
              // Reset both counters: this is an expected expiry, not an error
              reconnectCountRef.current = 0;
              apiErrorCountRef.current  = 0;
              scheduleReconnect(null);
            }
          } catch { /* malformed payload — ignore */ }
        });

        await room.connect(livekitUrl, livekitToken);
        if (localCancelled || cancelledRef.current) return;

        updateStatus("connected");
        // Reset reconnect counter on a successful connection
        reconnectCountRef.current = 0;

        // Speak any text that was queued before/during reconnect
        const queued = pendingTextRef.current;
        pendingTextRef.current = null;
        if (queued) {
          // Brief pause so tracks have time to subscribe
          await new Promise((r) => setTimeout(r, 600));
          if (!localCancelled && !cancelledRef.current &&
              room.state === ConnectionState.Connected) {
            room.localParticipant
              .publishData(mkPayload("avatar.speak_text", { text: queued }), { topic: "agent-control" })
              .catch(() => {});
          }
        }

      } catch (err) {
        // LiveKit room.connect() or other unexpected error — use normal reconnect
        console.error("[LiveAvatarVideo] connection error:", err);
        if (!localCancelled && !cancelledRef.current) {
          scheduleReconnect(null);
        }
      }
    })();

    return () => {
      localCancelled = true;
      // Don't set cancelledRef here — that would prevent reconnects triggered
      // by scheduleReconnect (which bumps connectKey). Only the unmount cleanup does that.
      speakResolveRef.current?.();
      speakResolveRef.current = null;
      roomRef.current?.disconnect();
      roomRef.current = null;
    };
    // connectKey changes on every reconnect attempt — this re-runs the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectKey]);

  // ── Final unmount cleanup ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      speakResolveRef.current?.();
      speakResolveRef.current = null;
      roomRef.current?.disconnect();
      roomRef.current = null;
    };
  }, []);

  // ── render ─────────────────────────────────────────────────────────────────

  const connected = status === "connected";

  return (
    <>
      {/* Live lip-sync video — shown only when connected */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className={className}
        style={{ ...style, display: connected ? undefined : "none" }}
      />

      {/* Static fallback — shown while connecting, on error, or in dev/TTS mode */}
      {(!connected || devMode) && (
        <img
          src={fallbackSrc}
          alt={fallbackAlt}
          className={className}
          style={style}
        />
      )}

      {/* Dev-mode badge */}
      {devMode && (
        <div style={{
          position: "absolute", bottom: 130, left: "50%", transform: "translateX(-50%)",
          zIndex: 10, padding: "3px 12px", borderRadius: 20,
          background: "rgba(0,0,0,0.7)", border: "1px solid rgba(99,102,241,0.5)",
          color: "#a5b4fc", fontFamily: "monospace", fontSize: 9,
          letterSpacing: "0.18em", textTransform: "uppercase", whiteSpace: "nowrap",
        }}>
          🔇 dev · browser tts
        </div>
      )}

      {/* Status badge */}
      {(status === "connecting") && (
        <div
          style={{
            position: "absolute",
            bottom: 130,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            padding: "4px 14px",
            borderRadius: 20,
            background: "rgba(0,0,0,0.75)",
            border: "1px solid rgba(230,57,70,0.35)",
            color: "var(--kl-red,#e63946)",
            fontFamily: "monospace",
            fontSize: 10,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            animation: "la-blink 1.4s ease-in-out infinite",
          }}
        >
          {reconnectCountRef.current > 0
            ? `Reconnecting… (${reconnectCountRef.current}/${MAX_RECONNECTS})`
            : "Live avatar connecting…"}
        </div>
      )}

      {status === "error" && (
        <div
          style={{
            position: "absolute",
            bottom: 100,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{
            padding: "4px 14px",
            borderRadius: 20,
            background: "rgba(0,0,0,0.75)",
            border: "1px solid rgba(255,100,100,0.4)",
            color: "#f87171",
            fontFamily: "monospace",
            fontSize: 10,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}>
            Avatar offline — text interview active
          </div>
          <button
            onClick={retryNow}
            style={{
              background: "linear-gradient(135deg,#e63946,#c1121f)",
              color: "#fff", border: "none", borderRadius: 8,
              padding: "5px 16px", fontSize: 11, fontWeight: 700,
              cursor: "pointer", letterSpacing: "0.05em",
              boxShadow: "0 2px 12px rgba(230,57,70,0.4)",
            }}
          >
            ↻ Retry Avatar
          </button>
        </div>
      )}

      <style>{`
        @keyframes la-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.45; }
        }
      `}</style>
    </>
  );
});

export default LiveAvatarVideo;
